import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const CLARA_ORIGIN = 'https://clarahospital.com'
const QORIAX_ORIGIN = 'https://www.qoriax.com'

const CLARA_PUBLIC = {
  '/': {
    title: 'Centro Médico Santa Clara | Nueva Esperanza, Canindeyú',
    description: 'Centro Médico Santa Clara en Nueva Esperanza, Canindeyú, Paraguay. Consultas médicas, especialidades, laboratorio, imágenes y agendamiento.',
  },
  '/medicos': {
    title: 'Médicos y Especialistas | Centro Médico Santa Clara',
    description: 'Conocé a los médicos y especialistas del Centro Médico Santa Clara en Nueva Esperanza, Canindeyú.',
  },
  '/agenda': {
    title: 'Agendar Consulta | Centro Médico Santa Clara',
    description: 'Agendá tu consulta en el Centro Médico Santa Clara. Elegí especialidad, profesional, fecha y horario disponible.',
  },
  '/nosotros': {
    title: 'Nosotros | Centro Médico Santa Clara',
    description: 'Conocé el Centro Médico Santa Clara, su propuesta asistencial y su compromiso con Nueva Esperanza y Canindeyú.',
  },
  '/aprende': {
    title: 'Aprende Salud | Centro Médico Santa Clara',
    description: 'Información de salud y prevención preparada para pacientes y familias del Centro Médico Santa Clara.',
  },
  '/galeria': {
    title: 'Galería | Centro Médico Santa Clara',
    description: 'Conocé las instalaciones y espacios asistenciales del Centro Médico Santa Clara.',
  },
  '/privacidad': {
    title: 'Política de Privacidad | Centro Médico Santa Clara',
    description: 'Información sobre el tratamiento de datos personales en los canales digitales del Centro Médico Santa Clara.',
  },
  '/privacy': {
    title: 'Política de Privacidad | Centro Médico Santa Clara',
    description: 'Información sobre el tratamiento de datos personales en los canales digitales del Centro Médico Santa Clara.',
    canonicalPath: '/privacidad',
  },
  '/terminos': {
    title: 'Términos de Uso | Centro Médico Santa Clara',
    description: 'Condiciones de uso de los servicios digitales públicos del Centro Médico Santa Clara.',
  },
  '/terms': {
    title: 'Términos de Uso | Centro Médico Santa Clara',
    description: 'Condiciones de uso de los servicios digitales públicos del Centro Médico Santa Clara.',
    canonicalPath: '/terminos',
  },
}

const QORIAX_PUBLIC = {
  '/': {
    title: 'QorIAX — Infraestructura Inteligente Empresarial',
    description: 'QorIAX integra automatización, inteligencia aplicada y operación multi-tenant con aislamiento estricto por organización.',
  },
  '/privacidad': {
    title: 'Política de Privacidad | QorIAX',
    description: 'Cómo QorIAX protege y separa los datos procesados por sus servicios digitales.',
  },
  '/privacy': {
    title: 'Política de Privacidad | QorIAX',
    description: 'Cómo QorIAX protege y separa los datos procesados por sus servicios digitales.',
    canonicalPath: '/privacidad',
  },
  '/terminos': {
    title: 'Términos de Uso | QorIAX',
    description: 'Condiciones de uso de la plataforma y de los servicios digitales públicos de QorIAX.',
  },
  '/terms': {
    title: 'Términos de Uso | QorIAX',
    description: 'Condiciones de uso de la plataforma y de los servicios digitales públicos de QorIAX.',
    canonicalPath: '/terminos',
  },
}

const PRIVATE_PREFIXES = [
  '/app',
  '/gestion',
  '/portal',
  '/patient-portal',
  '/mis-turnos',
  '/quirofano',
  '/teleconsulta',
  '/internacion',
  '/login',
  '/saas',
]

function isQoriaxHost() {
  if (typeof window === 'undefined') return false
  const h = window.location.hostname.replace(/^www\./, '').toLowerCase()
  return h === 'qoriax.com' || h === 'coriex.com' || h.startsWith('saas.qoriax') || h.includes('qoriax-main-deploy')
}

function upsertMeta(selector, attributes) {
  let el = document.head.querySelector(selector)
  if (!el) {
    el = document.createElement(selector.startsWith('link') ? 'link' : 'meta')
    document.head.appendChild(el)
  }
  Object.entries(attributes).forEach(([key, value]) => el.setAttribute(key, value))
}

export default function PublicSeo() {
  const location = useLocation()

  useEffect(() => {
    const pathname = location.pathname || '/'
    const privateRoute = PRIVATE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
    const qoriax = isQoriaxHost()

    let title
    let description
    let canonical
    let robots

    if (qoriax) {
      const isSaasHost = typeof window !== 'undefined' && window.location.hostname.toLowerCase().startsWith('saas.qoriax')
      const meta = QORIAX_PUBLIC[pathname]
      const qoriaxOrigin = isSaasHost ? 'https://saas.qoriax.com' : QORIAX_ORIGIN
      if (meta) {
        title = meta.title
        description = meta.description
        canonical = `${qoriaxOrigin}${meta.canonicalPath || pathname}`
      } else {
        title = 'QorIAX — Acceso seguro'
        description = 'Acceso seguro a la plataforma QorIAX.'
        canonical = `${qoriaxOrigin}${pathname}`
      }
      robots = isSaasHost || privateRoute || !meta ? 'noindex,nofollow,noarchive' : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
    } else {
      const meta = CLARA_PUBLIC[pathname]
      if (meta) {
        title = meta.title
        description = meta.description
        canonical = `${CLARA_ORIGIN}${meta.canonicalPath || pathname}`
        robots = 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
      } else {
        title = 'Página no encontrada | Centro Médico Santa Clara'
        description = 'La página solicitada no corresponde a una página pública del Centro Médico Santa Clara.'
        canonical = `${CLARA_ORIGIN}${pathname}`
        robots = 'noindex,nofollow'
      }
      if (privateRoute) robots = 'noindex,nofollow'
    }

    document.title = title
    document.documentElement.lang = 'es-PY'
    upsertMeta('meta[name="description"]', { name: 'description', content: description })
    upsertMeta('meta[name="robots"]', { name: 'robots', content: robots })
    upsertMeta('link[rel="canonical"]', { rel: 'canonical', href: canonical })
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title })
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description })
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical })
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title })
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description })
  }, [location.pathname])

  return null
}
